import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { describeRule, type FieldConfig, type FormField, type MapsTo, type Rule } from '@re/form-engine';
import { UNITS, type Lookups } from '@re/form-ui';
import {
  Alert,
  Box,
  Chip,
  Divider,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { TextField } from '../../components/text-field';
import { useState, type ReactNode } from 'react';
import { FIELD_TYPE, MAPS_TO_LABEL } from '../../lib/labels';
import { FieldIcon, FIELD_META } from './fieldMeta';
import { availableMapsTo, hasStaticOptions, isDisplay, toKey } from './helpers';
import { RuleEditor } from './RuleEditor';
import { OptionsEditor } from './OptionsEditor';
import { CountriesPicker, RequestTypesPicker } from './FormScope';
import { QuickVisibility } from './QuickVisibility';
import { buildRule, dimFields, fromRestRule, parseQuick, restRule } from './visibility';
import { BilingualField, fromLocalized, toLocalized } from '../../components/bilingual';
import { isEn, tr } from '../../lib/i18n';

interface Props {
  field: FormField;
  fields: FormField[];
  lookups?: Lookups;
  issues: { message: string; level: 'error' | 'warning' }[];
  onChange: (f: FormField) => void;
  onDelete: () => void;
  onDuplicate: () => void;
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
      {hint && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{hint}</Typography>}
      <Box sx={{ mt: hint ? 0 : 1 }}>{children}</Box>
    </Box>
  );
}

type EditorTab = 'basic' | 'options' | 'settings' | 'logic';
/** the editor reopens on the tab you used last (fast when setting conditions on many fields) */
let lastTab: EditorTab = 'basic';

export function FieldEditor({ field: f, fields, lookups, issues, onChange, onDelete, onDuplicate }: Props) {
  const [tab, setTabState] = useState<EditorTab>(() => lastTab);
  const setTab = (t: EditorTab) => {
    lastTab = t;
    setTabState(t);
  };
  const idx = fields.findIndex((x) => x.key === f.key);
  const previous = fields.slice(0, idx);
  const set = (patch: Partial<FormField>) => onChange({ ...f, ...patch });
  const cfg = (patch: Partial<FieldConfig>) => onChange({ ...f, config: { ...f.config, ...patch } });
  const val = (patch: Record<string, number | string | undefined>) => {
    const v = { ...(f.config.validation ?? {}), ...patch };
    Object.keys(v).forEach((k) => (v as Record<string, unknown>)[k] === undefined && delete (v as Record<string, unknown>)[k]);
    cfg({ validation: Object.keys(v).length ? v : undefined });
  };
  const num = (s: string) => (s === '' ? undefined : Number(s));
  const labelOf = (key: string) => fields.find((x) => x.key === key)?.label.ar || key;
  const mapsOptions = availableMapsTo(f, fields);
  const display = isDisplay(f.type);
  const isNumeric = ['number', 'currency', 'number_range'].includes(f.type);
  const isText = ['text', 'textarea'].includes(f.type);
  const hasSettings = isNumeric || isText || ['checkbox_group', 'image_upload', 'file_upload', 'location'].includes(f.type) || !display;
  const logicCount = (f.visibleWhen ? 1 : 0) + (f.requiredWhen ? 1 : 0);
  const errors = issues.filter((i) => i.level === 'error');
  const warnings = issues.filter((i) => i.level === 'warning');
  const dims = dimFields(fields, idx);
  const quick = parseQuick(f.visibleWhen, dims, lookups);
  const current = tab === 'options' && !hasStaticOptions(f.type) ? 'basic' : tab === 'settings' && !hasSettings ? 'basic' : tab;

  return (
    <Stack spacing={2}>
      {/* header */}
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: 'primary.main', color: 'primary.contrastText', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          <FieldIcon type={f.type} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" noWrap>{f.label.ar || tr('حقل بدون عنوان', 'Untitled field')}</Typography>
          {f.label.en && <Typography variant="body2" color="text.secondary" noWrap><bdi>{f.label.en}</bdi></Typography>}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary">{FIELD_TYPE[f.type]} — {FIELD_META[f.type].hint}</Typography>
          </Stack>
        </Box>
        <Tooltip title={tr('تكرار الحقل', 'Duplicate field')}>
          <IconButton onClick={onDuplicate} disabled={f.type === 'request_type'}><ContentCopyIcon /></IconButton>
        </Tooltip>
        <Tooltip title={tr('حذف الحقل', 'Delete field')}>
          <IconButton color="error" onClick={onDelete}><DeleteOutlineIcon /></IconButton>
        </Tooltip>
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip size="small" variant="outlined" label={f.key} sx={{ fontFamily: 'monospace' }} />
        {!display && <Chip size="small" color={f.required ? 'error' : 'default'} variant={f.required ? 'filled' : 'outlined'} label={f.required ? tr('إلزامي', 'Required') : tr('اختياري', 'Optional')} />}
        {f.mapsTo ? <Chip size="small" color="primary" variant="outlined" label={tr(`يُحفظ في: ${MAPS_TO_LABEL[f.mapsTo]}`, `Stored in: ${MAPS_TO_LABEL[f.mapsTo]}`)} /> : !display && <Chip size="small" variant="outlined" label={tr('حقل مخصص', 'Custom field')} />}
        {f.visibleWhen && <Chip size="small" color="secondary" label={tr('ظهور مشروط', 'Conditional visibility')} />}
        {f.requiredWhen && <Chip size="small" color="secondary" variant="outlined" label={tr('إلزام مشروط', 'Conditionally required')} />}
      </Stack>

      {errors.length > 0 && <Alert severity="error">{errors.map((e, i) => <div key={i}>{e.message}</div>)}</Alert>}
      {warnings.length > 0 && <Alert severity="warning">{warnings.map((e, i) => <div key={i}>{e.message}</div>)}</Alert>}

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <Tabs value={current} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
          <Tab value="basic" label={tr('الأساسيات', 'Basics')} />
          {hasStaticOptions(f.type) && <Tab value="options" label={`${tr('الخيارات', 'Options')} (${f.config.options?.length ?? 0})`} />}
          {hasSettings && <Tab value="settings" label={tr('التحقق والإعدادات', 'Validation & settings')} />}
          {f.type !== 'request_type' && (
            <Tab value="logic" label={`${tr('الشروط', 'Conditions')}${logicCount ? ` (${logicCount})` : ''}`} />
          )}
        </Tabs>

        <Box sx={{ p: 2 }}>
          {current === 'basic' && (
            <Stack spacing={2.5}>
              <Section title={tr('العنوان', 'Label')} hint={tr('كما يراه الموظف والعميل', 'As staff and customers see it')}>
                <BilingualField
                  fullWidth
                  label={tr('عنوان الحقل', 'Field label')}
                  value={fromLocalized(f.label)}
                  onChange={(v) => {
                    // the technical key is generated from the English title (Latin letters only)
                    const prev = f.label.en ? toKey(f.label.en) : '';
                    const following = /^(field|section)_\d+$/.test(f.key) || (!!prev && f.key === prev);
                    const auto = following && v.en ? toKey(v.en) : f.key;
                    set({
                      label: { ar: v.ar, ...(v.en ? { en: v.en } : {}) },
                      key: auto && !fields.some((x) => x !== f && x.key === auto) ? auto : f.key,
                    });
                  }}
                  errorAr={!f.label.ar ? tr('مطلوب', 'Required') : undefined}
                />
              </Section>

              {f.type === 'request_type' && (
                <Section title={tr('ماذا يستقبل هذا النموذج؟', 'What does this form take?')}>
                  <RequestTypesPicker lookups={lookups} value={f.config.options} onChange={(options) => cfg({ options })} />
                </Section>
              )}

              {!display && (
                <Section title={tr('الإلزام', 'Requirement')}>
                  <FormControlLabel
                    control={<Switch checked={f.required} disabled={f.type === 'request_type'} onChange={(e) => set({ required: e.target.checked })} />}
                    label={f.type === 'request_type' ? tr('نوع الطلب إلزامي دائماً', 'Request type is always required') : tr('إلزامي دائماً عند ظهوره', 'Always required when shown')}
                  />
                </Section>
              )}

              {!display && (
                <Section title={tr('أين تُحفظ القيمة؟', 'Where is the value stored?')} hint={tr('الحقل النظامي يُحفظ في عمود مفهرس (للبحث والفلترة والإحصاء)، والمخصص في بيانات إضافية', 'System fields are stored in an indexed column (for search, filtering and stats); custom fields go into extra data')}>
                  <TextField select fullWidth label={tr('الحفظ في', 'Store in')} value={f.mapsTo ?? ''} onChange={(e) => set({ mapsTo: (e.target.value || null) as MapsTo | null })}>
                    <MenuItem value="">{tr('حقل مخصص', 'Custom field')}</MenuItem>
                    {f.mapsTo && !mapsOptions.includes(f.mapsTo) && <MenuItem value={f.mapsTo}>{MAPS_TO_LABEL[f.mapsTo]}</MenuItem>}
                    {mapsOptions.map((m) => <MenuItem key={m} value={m}>{MAPS_TO_LABEL[m]}</MenuItem>)}
                  </TextField>
                </Section>
              )}

              <Section title={tr('العرض', 'Display')}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth label={tr('القسم / الخطوة', 'Section / step')} value={f.section ?? ''} onChange={(e) => set({ section: e.target.value || null })} helperText={tr('مثال: basics, details, contact', 'e.g. basics, details, contact')} slotProps={{ htmlInput: { dir: 'ltr' } }} />
                  </Grid>
                  {!display && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField select fullWidth label={tr('العرض', 'Width')} value={f.config.width ?? 'full'} onChange={(e) => cfg({ width: e.target.value as FieldConfig['width'] })} helperText=" ">
                        <MenuItem value="full">{tr('عرض كامل', 'Full width')}</MenuItem>
                        <MenuItem value="half">{tr('نصف العرض', 'Half width')}</MenuItem>
                        <MenuItem value="third">{tr('ثلث العرض', 'One third')}</MenuItem>
                      </TextField>
                    </Grid>
                  )}
                </Grid>
              </Section>

              <Divider />
              <TextField
                fullWidth
                label={tr('المفتاح التقني (key)', 'Technical key')}
                value={f.key}
                onChange={(e) => set({ key: toKey(e.target.value) || f.key })}
                helperText={tr('مفتاح تقني (حروف لاتينية وأرقام) — يُولَّد تلقائياً من العنوان عند الإنشاء.', 'Technical key (Latin letters and digits) — generated automatically from the label on creation.')}
                slotProps={{ htmlInput: { dir: 'ltr' } }}
              />
            </Stack>
          )}

          {current === 'options' && (
            <OptionsEditor
              field={f}
              onChange={(options) => cfg({ options })}
              audience={dims.request_type && lookups ? { key: dims.request_type.key, types: lookups.requestTypes.map((r) => ({ code: r.code, label: (isEn() ? r.name_en : r.name_ar) || r.name_ar })) } : undefined}
            />
          )}

          {current === 'settings' && (
            <Grid container spacing={2}>
              {isNumeric && (
                <>
                  <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth type="number" label={tr('الحد الأدنى', 'Minimum')} value={f.config.validation?.min ?? ''} onChange={(e) => val({ min: num(e.target.value) })} /></Grid>
                  <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth type="number" label={tr('الحد الأعلى', 'Maximum')} value={f.config.validation?.max ?? ''} onChange={(e) => val({ max: num(e.target.value) })} /></Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField select fullWidth label={tr('الوحدة', 'Unit')} value={f.config.unit ?? ''} onChange={(e) => cfg({ unit: e.target.value || undefined })}>
                      <MenuItem value="">—</MenuItem>
                      {Object.entries(UNITS).map(([k, u]) => (
                        <MenuItem key={k} value={k}>{tr(u.ar, u.en)}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  {f.type === 'number' && (
                    <Grid size={12}><FormControlLabel control={<Switch checked={f.config.validation?.step === 1} onChange={(e) => val({ step: e.target.checked ? 1 : undefined })} />} label={tr('أعداد صحيحة فقط', 'Whole numbers only')} /></Grid>
                  )}
                </>
              )}
              {isText && (
                <>
                  <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth type="number" label={tr('أقل طول', 'Min length')} value={f.config.validation?.minLength ?? ''} onChange={(e) => val({ minLength: num(e.target.value) })} /></Grid>
                  <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth type="number" label={tr('أقصى طول', 'Max length')} value={f.config.validation?.maxLength ?? ''} onChange={(e) => val({ maxLength: num(e.target.value) })} /></Grid>
                  <Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth label={tr('نمط (Regex)', 'Pattern (regex)')} value={f.config.validation?.pattern ?? ''} onChange={(e) => val({ pattern: e.target.value || undefined })} slotProps={{ htmlInput: { dir: 'ltr' } }} /></Grid>
                </>
              )}
              {f.type === 'checkbox_group' && (
                <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth type="number" label={tr('أقصى عدد اختيارات', 'Max selections')} value={f.config.validation?.max ?? ''} onChange={(e) => val({ max: num(e.target.value) })} /></Grid>
              )}
              {['image_upload', 'file_upload'].includes(f.type) && (
                <>
                  <Grid size={6}><TextField fullWidth type="number" label={tr('أقصى عدد ملفات', 'Max files')} value={f.config.maxFiles ?? ''} onChange={(e) => cfg({ maxFiles: num(e.target.value) })} /></Grid>
                  <Grid size={6}><TextField fullWidth type="number" label={tr('أقصى حجم (MB)', 'Max size (MB)')} value={f.config.maxSizeMb ?? ''} onChange={(e) => cfg({ maxSizeMb: num(e.target.value) })} /></Grid>
                </>
              )}
              {f.type === 'location' && (
                <>
                  <Grid size={12}>
                    <CountriesPicker value={f.config.countries} onChange={(countries) => cfg({ countries })} />
                  </Grid>
                  <Grid size={6}>
                    <TextField select fullWidth label={tr('عمق الاختيار', 'Selection depth')} value={f.config.depth ?? 'neighborhood'} onChange={(e) => cfg({ depth: e.target.value as FieldConfig['depth'] })}>
                      <MenuItem value="governorate">{tr('حتى المحافظة', 'Down to governorate')}</MenuItem>
                      <MenuItem value="area">{tr('حتى المنطقة', 'Down to area')}</MenuItem>
                      <MenuItem value="neighborhood">{tr('حتى الحي', 'Down to neighborhood')}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid size={6}>
                    <TextField select fullWidth label={tr('العمق الإلزامي', 'Required depth')} value={f.config.requiredDepth ?? 'country'} onChange={(e) => cfg({ requiredDepth: e.target.value as FieldConfig['requiredDepth'] })}>
                      <MenuItem value="country">{tr('الدولة', 'Country')}</MenuItem>
                      <MenuItem value="governorate">{tr('المحافظة', 'Governorate')}</MenuItem>
                      <MenuItem value="area">{tr('المنطقة', 'Area')}</MenuItem>
                      <MenuItem value="neighborhood">{tr('الحي', 'Neighborhood')}</MenuItem>
                    </TextField>
                  </Grid>
                </>
              )}
              {!display && (
                <>
                  <Grid size={12}><Divider /></Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <BilingualField fullWidth required={false} label={tr('نص توضيحي داخل الحقل', 'Placeholder text')} value={fromLocalized(f.config.placeholder)} onChange={(v) => cfg({ placeholder: toLocalized(v) })} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <BilingualField fullWidth required={false} label={tr('مساعدة تحت الحقل', 'Help text below the field')} value={fromLocalized(f.config.help)} onChange={(v) => cfg({ help: toLocalized(v) })} />
                  </Grid>
                </>
              )}
            </Grid>
          )}

          {current === 'logic' && (
            <Stack spacing={3}>
              {quick ? (
                <>
                  <Section title={tr('من يرى هذا الحقل؟', 'Who sees this field?')} hint={tr('اضغط لاختيار أو إلغاء. لا شيء محدد = يظهر للجميع. الحقل المخفي لا يُرسل ولا يُتحقق منه.', 'Tap to include or exclude. Nothing narrowed = shown to everyone. Hidden fields are not submitted or validated.')}>
                    <QuickVisibility value={quick} dims={dims} lookups={lookups} onChange={(q) => set({ visibleWhen: buildRule(q, dims, lookups) })} />
                  </Section>
                  <Section title={tr('شروط إضافية', 'More conditions')} hint={tr('على أي حقل قبله — مثل: يظهر إذا كان عدد الغرف أكثر من 3. تُجمع مع ما سبق بـ «و».', 'On any earlier field — e.g. shown when rooms is more than 3. Combined with the above using AND.')}>
                    <RuleEditor value={restRule(quick.rest)} onChange={(r: Rule | null) => set({ visibleWhen: buildRule({ ...quick, rest: fromRestRule(r) }, dims, lookups) })} candidates={previous} lookups={lookups} emptyLabel={tr('لا يوجد', 'None')} labelOf={labelOf} />
                  </Section>
                </>
              ) : (
                <Section title={tr('متى يظهر هذا الحقل؟', 'When is this field shown?')} hint={tr('شرط متقدم (أو/مجموعات) — يُعدّل هنا. يعتمد فقط على الحقول التي قبله.', 'Advanced rule (OR/groups) — edit it here. Depends only on earlier fields.')}>
                  <RuleEditor value={f.visibleWhen} onChange={(r) => set({ visibleWhen: r })} candidates={previous} lookups={lookups} emptyLabel={tr('يظهر دائماً', 'Always shown')} labelOf={labelOf} />
                </Section>
              )}
              {f.visibleWhen && <Alert severity="info" icon={false} sx={{ py: 0.5 }}>{describeRule(f.visibleWhen, labelOf)}</Alert>}
              {!display && (
                <>
                  <Divider />
                  <Section title={tr('متى يصبح إلزامياً؟', 'When does it become required?')} hint={tr('اختياري — إضافةً إلى خيار «إلزامي دائماً».', 'Optional — in addition to the “Always required” switch.')}>
                    <RuleEditor value={f.requiredWhen} onChange={(r) => set({ requiredWhen: r })} candidates={previous} lookups={lookups} emptyLabel={tr('غير مشروط', 'Unconditional')} labelOf={labelOf} />
                  </Section>
                </>
              )}
            </Stack>
          )}
        </Box>
      </Paper>
      {f.type !== 'request_type' && current !== 'logic' && (
        <Typography variant="caption" color="text.secondary">
          {describeRule(f.visibleWhen, labelOf)}
        </Typography>
      )}
    </Stack>
  );
}
